// ATAD-134 — detecting audiences that are broader than the user probably meant.
//
// Two independent checks live here:
//
//   1. WIDE-OPEN FILTERS. A range left at its absolute min..max, or a
//      multi-select with every option ticked, doesn't narrow anything. This
//      matters more than it sounds: Range.tsx seeds a freshly dropped card with
//      `{min: attrs.min, max: attrs.max}`, so an attribute dragged in and never
//      touched is wide open by default — the exact mistake the ticket is about.
//
//   2. OVERSIZED AUDIENCE. A record count that is an implausible share of the
//      US consumer universe.
//
// Both are PURE. No React, no network. That's possible because a VariableLeaf
// carries the attribute's domain (`attrs.min`/`attrs.max`/`optionsKey`) and the
// user's selection (`state`) on the same object.
//
// IMPORTANT: this must run on the CLIENT expression tree, never on the API
// payload. expressionTreeToApi (helpers/dataDictionary.ts) drops `attrs`, so
// once serialized there is nothing left to compare the selection against.
//
// ATAD-146 adds a third, model-specific check (findFullRangeModels): a model
// file left at its full 1–20 score range. Unlike the variable checks it runs
// in BOTH zones — see that function.

import {
  MODEL_SCORE_MAX,
  MODEL_SCORE_MIN,
  flattenLeaves,
  formatModelScoreRange,
  isFullModelScoreRange,
  isModelLeaf,
  isVariableLeaf,
} from './expressionTree';
import { getOptionsForKey, type SelectOption } from './multiSelectOptions';
import states from '../assets/data-dictionary/states.json';
import type { ExpressionNode, VariableState } from '../types/dataDictionary';

export type ZoneType = 'include' | 'exclude';

export type BreadthIssueKind =
  | 'wide-open-range'
  | 'all-options-selected'
  // ATAD-146: a model file at its full 1–20 score range.
  | 'model-full-range';

export type BreadthIssue = {
  /**
   * Variable issues: `${zoneType}:${variableId}` — the identity
   * SET_ATTRIBUTE_STATE matches on. Model issues: `${zoneType}:model:${fileId}`
   * (a model leaf has no variable).
   */
  key: string;
  nodeId: string;
  /** Variable issues only. */
  variableId?: string;
  /** What the warning names: the attribute, or the model's name. */
  attributeName: string;
  zoneType: ZoneType;
  kind: BreadthIssueKind;
  /** Numbers the warning copy needs: range bounds, or the option count. */
  detail: { min?: number; max?: number; total?: number };
};

type BreadthState = {
  expression: ExpressionNode | null;
  excludeExpression: ExpressionNode | null;
};

// FloatRange.tsx falls back to 0.1 when attrs.step is absent. Must match, or
// this helper and the widget disagree about what counts as "at the edge".
const DEFAULT_FLOAT_STEP = 0.1;

// multi-select-range enumerates every integer in [min, max]. Guard against a
// pathological domain (one dictionary range tops out at 2147440402) turning a
// click handler into an OOM.
const MAX_ENUMERABLE_SPAN = 1000;

/**
 * Normalize `state.values` to a Set of the values that actually go over the
 * wire.
 *
 * Two shapes occur. react-select writes `{value, label}` objects. A freshly
 * hydrated saved audience writes bare strings, until the card's mount effect
 * re-formats them — see isAlreadyFormatted in MultiSelectGeography.tsx. Both
 * normalize to the `value` side, which is what convertVariableLeafToApiLeaf
 * sends, so the comparison is identical either way.
 */
function normalizeSelectedValues(values: unknown): Set<string> {
  const out = new Set<string>();
  if (!Array.isArray(values)) return out;
  for (const entry of values) {
    if (entry == null) continue;
    if (typeof entry === 'object' && 'value' in (entry as Record<string, unknown>)) {
      out.add(String((entry as { value: unknown }).value));
    } else {
      out.add(String(entry));
    }
  }
  return out;
}

/**
 * True when every available option is selected.
 *
 * Compares value SETS rather than lengths. Same cost, and a length check would
 * be fooled by a duplicate entry — which is a false NEGATIVE, the expensive
 * direction here.
 */
function allOptionsSelected(
  state: VariableState['state'],
  options: SelectOption[]
): boolean {
  // A single-option picker hides its "Select All" and MultiSelectInput
  // auto-selects it, so "all selected" carries no intent worth warning about.
  if (options.length <= 1) return false;
  const selected = normalizeSelectedValues(state?.values);
  if (selected.size < options.length) return false;
  return options.every((option) => selected.has(String(option.value)));
}

/** Mirrors isStateField in MultiSelectGeography.tsx:72. Keep in lockstep. */
function isStateAttribute(v: VariableState): boolean {
  return (
    v.attributeName?.toLowerCase() === 'state' || v.attrs?.key === 'B2B_State'
  );
}

/**
 * Classify one variable, or null when it isn't wide open (or can't be judged).
 *
 * Cheap enough to call per-card from VariableCard if a future inline badge
 * wants it — unlike findWideOpenFilters, which walks the whole tree.
 */
export function isWideOpenVariable(
  v: VariableState | undefined | null
): BreadthIssueKind | null {
  if (!v?.attrs) return null;
  const { attrs, state } = v;

  switch (attrs.componentType) {
    case 'range': {
      if (!state || state.min == null || state.max == null) return null;
      const lo = Number(attrs.min);
      const hi = Number(attrs.max);
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo >= hi) return null;
      // <= / >= rather than ===: a saved audience whose stored bounds were
      // hydrated from a dictionary revision with a wider domain is still wide
      // open. Erring toward detection avoids a silent false negative.
      return Number(state.min) <= lo && Number(state.max) >= hi
        ? 'wide-open-range'
        : null;
    }

    case 'float-range': {
      if (!state || state.min == null || state.max == null) return null;
      const lo = parseFloat(attrs.min);
      const hi = parseFloat(attrs.max);
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo >= hi) return null;
      // Half a step of tolerance. FloatRange round-trips values through
      // `* multiplier` / `/ multiplier` to drive an integer-only slider, which
      // reintroduces binary-float noise (0.3 -> 0.30000000000000004). The
      // slider also cannot express anything between an endpoint and endpoint
      // +/- step/2, so nothing legitimate lives inside this band.
      const step = attrs.step ? parseFloat(attrs.step) : DEFAULT_FLOAT_STEP;
      const eps = Number.isFinite(step) && step > 0 ? step / 2 : 1e-9;
      return Number(state.min) <= lo + eps && Number(state.max) >= hi - eps
        ? 'wide-open-range'
        : null;
    }

    case 'multi-select-options':
      return allOptionsSelected(state, getOptionsForKey(attrs.optionsKey))
        ? 'all-options-selected'
        : null;

    case 'multi-select-range': {
      const lo = Number(attrs.min);
      const hi = Number(attrs.max);
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
      if (hi - lo + 1 > MAX_ENUMERABLE_SPAN) return null;
      const options: SelectOption[] = [];
      for (let i = lo; i <= hi; i += 1) {
        options.push({ value: String(i), label: String(i) });
      }
      return allOptionsSelected(state, options) ? 'all-options-selected' : null;
    }

    case 'multi-select': {
      // State is the only multi-select with an enumerable domain.
      //
      //   - City filters cities.json asynchronously and slices to 50 results,
      //     so "all cities" is never observable.
      //   - AIQ_BizID, B2B_BusinessName and carrier_route route here but match
      //     neither the state nor the city branch of MultiSelectGeography, so
      //     they render no input at all and can never hold values. Pre-existing
      //     bug; not ours to flag.
      if (!isStateAttribute(v)) return null;
      const options = (states as { name: string; code: string }[]).map((s) => ({
        value: s.code,
        label: s.name,
      }));
      return allOptionsSelected(state, options) ? 'all-options-selected' : null;
    }

    case 'boolean':
    default:
      // YesNo renders a label only — the attribute's presence IS the filter,
      // so there is no selection that could be "wide open".
      return null;
  }
}

function detailFor(v: VariableState, kind: BreadthIssueKind) {
  if (kind === 'wide-open-range') {
    return { min: Number(v.attrs.min), max: Number(v.attrs.max) };
  }
  if (v.attrs.componentType === 'multi-select') {
    return { total: (states as unknown[]).length };
  }
  if (v.attrs.componentType === 'multi-select-range') {
    return { total: Number(v.attrs.max) - Number(v.attrs.min) + 1 };
  }
  return { total: getOptionsForKey(v.attrs.optionsKey).length };
}

/**
 * Every wide-open filter in the builder, in document order.
 *
 * Defaults to the INCLUDE zone only. A wide-open range in the exclude zone
 * excludes nearly everyone — the audience collapses toward zero, which is the
 * opposite failure and deserves different copy. Issues carry `zoneType` so
 * that can be revisited without changing this signature.
 */
export function findWideOpenFilters(
  state: BreadthState | null | undefined,
  opts?: { zones?: ZoneType[] }
): BreadthIssue[] {
  if (!state) return [];
  const zones = opts?.zones ?? (['include'] as ZoneType[]);
  const issues: BreadthIssue[] = [];

  for (const zoneType of zones) {
    const root =
      zoneType === 'include' ? state.expression : state.excludeExpression;
    for (const leaf of flattenLeaves(root)) {
      if (!isVariableLeaf(leaf)) continue;
      const kind = isWideOpenVariable(leaf.variable);
      if (!kind) continue;
      issues.push({
        key: `${zoneType}:${leaf.variable.id}`,
        nodeId: leaf.nodeId,
        variableId: leaf.variable.id,
        attributeName: leaf.variable.attributeName,
        zoneType,
        kind,
        detail: detailFor(leaf.variable, kind),
      });
    }
  }

  return issues;
}

/**
 * ATAD-146: model leaves left at their full 1–20 score range, in document
 * order, checked in BOTH zones.
 *
 * A model file scores the whole US population, so its full range is everyone.
 * In Include that doesn't narrow the audience at all. In Exclude it removes
 * everyone in the model — nearly the whole universe — which is why, unlike
 * findWideOpenFilters (include only, so variables keep their behavior), this
 * looks at the exclude zone too: there it is the more damaging mistake.
 *
 * Keyed by zone + fileId, and deduplicated on that key: the same model
 * dropped into a zone twice at full range is one problem, not two.
 */
export function findFullRangeModels(
  state: BreadthState | null | undefined
): BreadthIssue[] {
  if (!state) return [];
  const issues: BreadthIssue[] = [];
  const seen = new Set<string>();

  for (const zoneType of ['include', 'exclude'] as ZoneType[]) {
    const root =
      zoneType === 'include' ? state.expression : state.excludeExpression;
    for (const leaf of flattenLeaves(root)) {
      if (!isModelLeaf(leaf) || !isFullModelScoreRange(leaf)) continue;
      const key = `${zoneType}:model:${leaf.fileId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      issues.push({
        key,
        nodeId: leaf.nodeId,
        attributeName: leaf.name || 'Model file',
        zoneType,
        kind: 'model-full-range',
        detail: { min: MODEL_SCORE_MIN, max: MODEL_SCORE_MAX },
      });
    }
  }

  return issues;
}

/**
 * ATAD-146: the warning text for a model-full-range issue, by zone. The two
 * zones fail in opposite directions, so they get different copy.
 */
export function fullRangeModelWarning(issue: BreadthIssue): string {
  const range = formatModelScoreRange(
    issue.detail.min ?? MODEL_SCORE_MIN,
    issue.detail.max ?? MODEL_SCORE_MAX,
  );
  return issue.zoneType === 'exclude'
    ? `score set to its full range (${range}), so this excludes everyone in ${issue.attributeName}`
    : `score set to its full range (${range}), so it doesn't narrow the audience`;
}

/**
 * Content hash of an issue set, used to warn once per DISTINCT problem.
 *
 * Order-independent so that regrouping or reordering leaves — neither of which
 * changes what's wrong — doesn't re-trigger a warning the user already
 * dismissed.
 */
export function wideOpenSignature(issues: BreadthIssue[]): string {
  return issues
    .map((i) => `${i.key}#${i.kind}`)
    .sort()
    .join('|');
}

// ---------------------------------------------------------------------------
// Oversized audience
// ---------------------------------------------------------------------------

export const OVERSIZE_RATIO_THRESHOLD = 0.5;

/**
 * Fraction of the US consumer universe an audience covers, or null when it
 * can't be computed. Null is meaningful: the universe size comes from a server
 * query that is allowed to fail, and an advisory warning must fail OPEN.
 */
export function universeShare(
  recordsAvailable: number | null | undefined,
  universeSize: number | null | undefined
): number | null {
  const records = Number(recordsAvailable);
  const universe = Number(universeSize);
  if (!Number.isFinite(records) || !Number.isFinite(universe)) return null;
  if (universe <= 0) return null;
  return records / universe;
}

export function isOversizedAudience(
  recordsAvailable: number | null | undefined,
  universeSize: number | null | undefined
): boolean {
  const share = universeShare(recordsAvailable, universeSize);
  return share != null && share > OVERSIZE_RATIO_THRESHOLD;
}
